import React from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  InputLabel,
  TextField,
} from "@mui/material";
import { useForm, SubmitHandler } from "react-hook-form";
import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import { emailRegex } from "../../../shared/validators";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import colors from "../../../shared/themes/colors";
import {
  handleLogin,
  togglePasswordVisibility,
} from "../../loginSlice";
import useTranslation from "../../../shared/hooks/useTranslation";
import FormNotificationMessage from "../../../shared/components/FormNotificationMessage";
import { Login } from "../../models/AccountModels";
import { dt } from "../../../shared/themes/redesignTokens";

interface LoginFormProps {
  onSubmitSuccess?: () => void;
  formId?: string;
  showForgotPassword?: boolean;
  showRegistration?: boolean;
  onForgotPassword?: () => void;
  onRegistration?: () => void;
  className?: string;
}

const LoginForm = ({
  onSubmitSuccess,
  formId = "loginForm",
  showForgotPassword = true,
  showRegistration = true,
  onForgotPassword,
  onRegistration,
  className = "",
}: LoginFormProps) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("account");
  const { translate: tCommon } = useTranslation("common");

  const {
    isLoading,
    formNotificationMessage,
    showPassword,
  } = useAppSelector((state) => state.login);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Login>();

  const watchedFields = watch(["userName", "password"]);
  const isFormValid = watchedFields[0] && watchedFields[1] && watchedFields[0].trim() !== "" && watchedFields[1].trim() !== "";

  const onSubmit: SubmitHandler<Login> = async (data: Login) => {
    const result = await dispatch(handleLogin(data));

    if (handleLogin.fulfilled.match(result)) {
      onSubmitSuccess?.();
    }
  };

  const handleClickShowPassword = () => {
    dispatch(togglePasswordVisibility());
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <Box className={className}>
      {formNotificationMessage && (
        <Box className="mb-3">
          <FormNotificationMessage {...formNotificationMessage} />
        </Box>
      )}

      <Box component="form" id={formId} onSubmit={handleSubmit(onSubmit)}>
        <Box>
          <InputLabel required>{translate("yourEmail")}</InputLabel>
          <TextField
            className="w-full"
            {...register("userName", {
              required: tCommon("forms.required"),
              pattern: { value: emailRegex, message: translate("invalidEmail") }
            })}
            error={!!errors.userName}
            helperText={errors.userName ? errors.userName.message : ""}
          />
        </Box>

        <Box className="mt-4">
          <InputLabel required>{translate("password")}</InputLabel>
          <TextField
            type={showPassword ? "text" : "password"}
            className="w-full"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                  >
                    {showPassword ? <VisibilityOutlined fontSize="small" /> : <VisibilityOffOutlined fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            {...register("password", { required: true })}
            error={!!errors.password}
            helperText={errors.password ? tCommon("forms.required") : ""}
          />
        </Box>

        <Box sx={{ width: "100%", padding: 0, mt: 2 }}>
          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={!isFormValid || isLoading}
          >
            {translate("logIn")}
          </Button>
        </Box>
      </Box>

      {(showForgotPassword || showRegistration) && (
        <Box className="flex flex-col justify-center items-center mt-5 gap-14" sx={{ px: "16px" }}>
          {showForgotPassword && (
            <Box className="flex flex-col md:flex-row items-center gap-1">
              <Box className="text-16-regular" sx={{ color: colors.secondary.gray20 }}>
                {translate("forgotPasswordQuestion")}
              </Box>
              <Button
                variant="text"
                onClick={onForgotPassword}
                sx={{
                  color: dt.blue,
                  textDecoration: "underline",
                  textTransform: "none !important",
                  padding: 0,
                  minWidth: "auto",
                }}
              >
                <Box sx={{ fontSize: "16px", fontWeight: 400, lineHeight: "24px", letterSpacing: "-0.032px", color: dt.blue, textDecoration: "underline" }}>
                  {translate("resetPassword")}
                </Box>
              </Button>
            </Box>
          )}

          {showRegistration && (
            <Box className="flex items-center gap-1">
              <Box className="text-16-regular" sx={{ color: colors.secondary.gray20 }}>
                {translate("notAMember")}
              </Box>
              <Button
                variant="text"
                onClick={onRegistration}
                sx={{
                  color: dt.blue,
                  textDecoration: "underline",
                  textTransform: "none !important",
                  padding: 0,
                  minWidth: "auto",
                }}
              >
                <Box sx={{ fontSize: "16px", fontWeight: 400, lineHeight: "24px", letterSpacing: "-0.032px", color: dt.blue, textDecoration: "underline" }}>
                  {translate("signUp")}
                </Box>
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default LoginForm;
